import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { taskService } from '@/services';
import {
    CategorizedTaskBuckets,
    createEmptyTaskBuckets,
    normalizeTaskBuckets,
} from '@/shared/components/tasks/buckets';
import { useUserStore, useWorkspaceStore } from '@/stores';

export const workspaceTaskQueryKeys = {
    all: ['workspace-tasks'] as const,
    buckets: (workspaceId?: string) => [...workspaceTaskQueryKeys.all, workspaceId ?? 'none', 'buckets'] as const,
};

interface UseWorkspaceTaskBucketsOptions {
    enabled?: boolean;
}

export const useWorkspaceTaskBuckets = ({ enabled = true }: UseWorkspaceTaskBucketsOptions = {}) => {
    const selectedWorkspaceId = useWorkspaceStore((state) => state.selectedWorkspace?.id);
    const activeWorkspaceId = useUserStore((state) => state.selectedUser?.activeWorkspace?.id);
    const workspaceId = selectedWorkspaceId ?? activeWorkspaceId;
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => workspaceTaskQueryKeys.buckets(workspaceId), [workspaceId]);
    const query = useQuery({
        enabled: enabled && Boolean(workspaceId),
        queryFn: async () => normalizeTaskBuckets(await taskService.listBuckets()),
        queryKey,
        staleTime: 30_000,
    });
    const setTaskBuckets = useCallback<Dispatch<SetStateAction<CategorizedTaskBuckets>>>(
        (nextTaskBuckets) => {
            queryClient.setQueryData<CategorizedTaskBuckets>(queryKey, (currentTaskBuckets) => {
                const current = currentTaskBuckets ?? createEmptyTaskBuckets();

                return typeof nextTaskBuckets === 'function' ? nextTaskBuckets(current) : nextTaskBuckets;
            });
        },
        [queryClient, queryKey],
    );

    return {
        ...query,
        taskBuckets: query.data ?? createEmptyTaskBuckets(),
        setTaskBuckets,
        workspaceId,
    };
};
